import React, { useState, useMemo } from 'react';
import { Appointment, AppointmentStatus } from '../../types/appointment';
import { Clock, User, Calendar, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { EnhancedAppointmentModal } from './EnhancedAppointmentModal';
import { PatientFlowViewModal } from './PatientFlowViewModal';
import { AwaitingAppointmentModal } from './AwaitingAppointmentModal';
import { WalkInAppointmentModal } from './WalkInAppointmentModal';
import { WaitlistCheckInModal } from './WaitlistCheckInModal';

interface PatientFlowViewProps {
  appointments: Appointment[];
  onUpdateStatus: (appointmentId: string, newStatus: AppointmentStatus) => Promise<void>;
  onSelectAppointment: (appointmentId: string) => void;
  teamMembers: any[];
  selectedVetIds: string[];
  onVetFilterChange: (vetIds: string[]) => void;
  loading?: boolean;
  onCreateClick?: () => void; // Optional callback for when Create button is clicked from parent
  onAppointmentCreated?: (appointment: any) => void; // Callback when a new appointment is created
}

// Status column configuration - subtle, professional colors like the calendar
const STATUS_COLUMNS = [
  {
    id: 'awaiting',
    title: 'Awaiting',
    backendStatus: AppointmentStatus.SCHEDULED,
    color: 'bg-gray-50 border-gray-200',
    headerColor: 'bg-gray-100 text-gray-800 border-b border-gray-200',
    cardColor: 'bg-white border border-gray-200',
  },
  {
    id: 'waitlist',
    title: 'Waitlist',
    backendStatus: AppointmentStatus.SCHEDULED,
    color: 'bg-gray-50 border-gray-200',
    headerColor: 'bg-gray-100 text-gray-800 border-b border-gray-200',
    cardColor: 'bg-white border border-gray-200',
    isWaitlist: true, // Special flag to identify walk-ins without assigned vet
  },
  {
    id: 'checked_in',
    title: 'Checked In',
    backendStatus: AppointmentStatus.CONFIRMED,
    color: 'bg-gray-50 border-gray-200',
    headerColor: 'bg-gray-100 text-gray-800 border-b border-gray-200',
    cardColor: 'bg-white border border-gray-200',
  },
  {
    id: 'in_progress',
    title: 'In Progress',
    backendStatus: AppointmentStatus.IN_PROGRESS,
    color: 'bg-gray-50 border-gray-200',
    headerColor: 'bg-gray-100 text-gray-800 border-b border-gray-200',
    cardColor: 'bg-white border border-gray-200',
  },
  {
    id: 'complete',
    title: 'Complete',
    backendStatus: AppointmentStatus.COMPLETE,
    color: 'bg-gray-50 border-gray-200',
    headerColor: 'bg-gray-100 text-gray-800 border-b border-gray-200',
    cardColor: 'bg-white border border-gray-200',
  },
  {
    id: 'cancelled',
    title: 'Cancelled',
    backendStatus: AppointmentStatus.CANCELLED,
    color: 'bg-gray-50 border-gray-200',
    headerColor: 'bg-gray-100 text-gray-800 border-b border-gray-200',
    cardColor: 'bg-white border border-red-200',
  },
];

export const PatientFlowView: React.FC<PatientFlowViewProps> = ({
  appointments,
  onUpdateStatus,
  onSelectAppointment,
  teamMembers,
  selectedVetIds,
  onVetFilterChange,
  loading = false,
  onCreateClick,
  onAppointmentCreated,
}) => {
  const [draggedAppointment, setDraggedAppointment] = useState<Appointment | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [checkInModalAppointment, setCheckInModalAppointment] = useState<Appointment | null>(null);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState<AppointmentStatus | null>(null);
  const [showEnhancedModal, setShowEnhancedModal] = useState(false);
  const [viewOnlyAppointment, setViewOnlyAppointment] = useState<Appointment | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [awaitingAppointment, setAwaitingAppointment] = useState<Appointment | null>(null);
  const [showAwaitingModal, setShowAwaitingModal] = useState(false);
  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [waitlistCheckInAppointment, setWaitlistCheckInAppointment] = useState<Appointment | null>(null);
  const [showWaitlistCheckInModal, setShowWaitlistCheckInModal] = useState(false);

  // Expose the modal open function to parent through the onCreateClick callback
  React.useEffect(() => {
    if (onCreateClick) {
      // Replace the parent's callback with one that opens our modal
      const originalOnCreateClick = onCreateClick;
      // Store reference to our modal opener
      (window as any)._patientFlowCreateHandler = () => setShowWalkInModal(true);
    }
    return () => {
      delete (window as any)._patientFlowCreateHandler;
    };
  }, [onCreateClick]);

  // Filter appointments by selected vets
  const filteredAppointments = useMemo(() => {
    if (selectedVetIds.length === 0) {
      return appointments;
    }
    // Always include waitlist appointments (no assigned vet), filter others by selected vets
    return appointments.filter(apt => 
      !apt.assigned_vet_user_id || selectedVetIds.includes(apt.assigned_vet_user_id)
    );
  }, [appointments, selectedVetIds]);

  // Group appointments by status
  const appointmentsByStatus = useMemo(() => {
    const grouped: Record<string, Appointment[]> = {
      waitlist: [],
      awaiting: [],
      checked_in: [],
      in_progress: [],
      complete: [],
      cancelled: [],
    };

    filteredAppointments.forEach((apt) => {
      // Separate waitlist (scheduled without assigned vet) from awaiting (scheduled with assigned vet)
      if (apt.status === AppointmentStatus.SCHEDULED) {
        if (!apt.assigned_vet_user_id || apt.title?.includes('Walk-in')) {
          grouped.waitlist.push(apt);
        } else {
          grouped.awaiting.push(apt);
        }
      } else {
        // All other statuses go to their respective columns
        const column = STATUS_COLUMNS.find(col => col.backendStatus === apt.status && !col.isWaitlist);
        if (column) {
          grouped[column.id].push(apt);
        }
      }
    });

    // Sort by appointment time within each column
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => 
        new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime()
      );
    });

    return grouped;
  }, [filteredAppointments]);

  const handleDragStart = (e: React.DragEvent, appointment: Appointment) => {
    setDraggedAppointment(appointment);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    setDraggedAppointment(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedAppointment) return;

    const targetColumn = STATUS_COLUMNS.find(col => col.id === columnId);
    if (!targetColumn) return;

    if (draggedAppointment.status === targetColumn.backendStatus) {
      return;
    }

    // Check if moving from waitlist to checked in
    const fromWaitlist = !draggedAppointment.assigned_vet_user_id && draggedAppointment.status === AppointmentStatus.SCHEDULED;
    const toCheckedIn = targetColumn.backendStatus === AppointmentStatus.CONFIRMED;
    
    if (fromWaitlist && toCheckedIn) {
      // Show the waitlist check-in modal (assign vet + time)
      setWaitlistCheckInAppointment(draggedAppointment);
      setPendingStatusUpdate(targetColumn.backendStatus);
      setShowWaitlistCheckInModal(true);
      return;
    }

    // Show modal when moving from Awaiting to Checked In
    const fromAwaiting = draggedAppointment.status === AppointmentStatus.SCHEDULED && draggedAppointment.assigned_vet_user_id;
    
    if (fromAwaiting && toCheckedIn) {
      // Show the enhanced modal for check-in - DON'T update status yet
      setCheckInModalAppointment(draggedAppointment);
      setPendingStatusUpdate(targetColumn.backendStatus); // Store pending status separately
      setShowEnhancedModal(true);
    } else {
      // Direct status update for other transitions
      try {
        await onUpdateStatus(draggedAppointment.id, targetColumn.backendStatus);
      } catch (error) {
        console.error('Failed to update appointment status:', error);
        alert('Failed to update appointment status. Please try again.');
      }
    }
  };

  const formatAppointmentTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return format(date, 'h:mm a');
    } catch {
      return 'Invalid time';
    }
  };

  const getAppointmentTypeColor = (appointment: any) => {
    const appointmentType = appointment.appointment_type?.toLowerCase() || '';
    
    // Use appointment_type for consistent coloring
    switch (appointmentType) {
      case 'checkup':
        return 'bg-emerald-400 border-emerald-500 text-white';
      case 'surgery':
        return 'bg-blue-500 border-blue-600 text-white';
      case 'emergency':
        return 'bg-orange-400 border-orange-500 text-white';
      case 'consultation':
        return 'bg-blue-400 border-blue-500 text-white';
      default:
        return 'bg-gray-400 border-gray-500 text-white';
    }
  };

  const handleVetToggle = (vetId: string) => {
    if (selectedVetIds.includes(vetId)) {
      onVetFilterChange(selectedVetIds.filter(id => id !== vetId));
    } else {
      onVetFilterChange([...selectedVetIds, vetId]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading patient flow...</div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* Kanban Board */}
      <div className="p-6">
        <div className="grid grid-cols-6 gap-4">
          {STATUS_COLUMNS.map((column) => {
            const columnAppointments = appointmentsByStatus[column.id];
            const isDropTarget = dragOverColumn === column.id;

            return (
              <div
                key={column.id}
                className={`flex flex-col rounded-lg border ${column.color} ${
                  isDropTarget ? 'ring-2 ring-blue-500 shadow-lg' : ''
                } transition-all duration-200`}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                {/* Column Header */}
                <div className={`${column.headerColor} px-4 py-2.5 rounded-t-lg`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm">{column.title}</h3>
                    <span className="text-xs font-medium px-2 py-0.5 bg-white rounded-full text-gray-600">
                      {columnAppointments.length}
                    </span>
                  </div>
                </div>

                {/* Appointment Cards */}
                <div className="flex-1 p-3 space-y-3 overflow-y-auto min-h-[400px]">
                  {columnAppointments.length === 0 ? (
                    <div className="text-center text-gray-400 text-sm mt-8">
                      No appointments
                    </div>
                  ) : (
                    columnAppointments.map((appointment) => (
                      <div
                        key={appointment.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, appointment)}
                        onDragEnd={handleDragEnd}
                        onClick={() => {
                          // Awaiting: Show contact-focused modal
                          // Other statuses: Show read-only view modal
                          if (appointment.status === AppointmentStatus.SCHEDULED) {
                            setAwaitingAppointment(appointment);
                            setShowAwaitingModal(true);
                          } else {
                            setViewOnlyAppointment(appointment);
                            setShowViewModal(true);
                          }
                        }}
                        className={`${column.cardColor} p-3 rounded-md shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md`}
                      >
                        {/* Appointment Time */}
                        <div className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                          <Clock className="w-4 h-4 mr-1.5 text-gray-500" />
                          {formatAppointmentTime(appointment.appointment_date)}
                        </div>

                        {/* Appointment Title */}
                        <div className="font-medium text-gray-900 mb-1">
                          {appointment.title}
                        </div>

                        {/* Pet Names */}
                        {appointment.pets && appointment.pets.length > 0 && (
                          <div className="text-xs text-gray-600 mb-2">
                            {appointment.pets.map(pet => pet.name).join(', ')}
                          </div>
                        )}

                        {/* Bottom Row: Type & Duration */}
                        <div className="flex items-center justify-between mt-2">
                          <span className={`text-xs px-2 py-1 rounded border ${getAppointmentTypeColor(appointment)}`}>
                            {appointment.appointment_type}
                          </span>
                          <span className="text-xs text-gray-500 font-medium">
                            {appointment.duration_minutes}m
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Enhanced Appointment Modal for Check-In (editable) - only shown on drag */}
      {showEnhancedModal && checkInModalAppointment && (
        <EnhancedAppointmentModal
          appointment={checkInModalAppointment}
          onClose={() => {
            setShowEnhancedModal(false);
            setCheckInModalAppointment(null);
            setPendingStatusUpdate(null);
          }}
          onSave={async (updatedAppointment) => {
            // The modal handles pet updates internally
            // Now update the appointment status to Checked In (if pending)
            if (pendingStatusUpdate && checkInModalAppointment) {
              try {
                await onUpdateStatus(checkInModalAppointment.id, pendingStatusUpdate);
              } catch (error) {
                console.error('Failed to update appointment status:', error);
                alert('Failed to update appointment status. Please try again.');
              }
            }
            // If no pending status, this was just a view/edit - refresh the data
            setShowEnhancedModal(false);
            setCheckInModalAppointment(null);
            setPendingStatusUpdate(null);
          }}
        />
      )}

      {/* Awaiting Modal - Front desk contact modal */}
      {showAwaitingModal && awaitingAppointment && (
        <AwaitingAppointmentModal
          appointment={awaitingAppointment}
          teamMembers={teamMembers}
          onClose={() => {
            setShowAwaitingModal(false);
            setAwaitingAppointment(null);
          }}
        />
      )}

      {/* View-Only Modal for Checked In and other statuses */}
      {showViewModal && viewOnlyAppointment && (
        <PatientFlowViewModal
          appointment={viewOnlyAppointment}
          onClose={() => {
            setShowViewModal(false);
            setViewOnlyAppointment(null);
          }}
        />
      )}

      {/* Walk-in Appointment Creation Modal */}
      {showWalkInModal && (
        <WalkInAppointmentModal
          onClose={() => setShowWalkInModal(false)}
          onSuccess={(newAppointment) => {
            setShowWalkInModal(false);
            // Optimistically add the new appointment instead of full reload
            if (newAppointment && onAppointmentCreated) {
              onAppointmentCreated(newAppointment);
            }
          }}
        />
      )}

      {/* Waitlist Check-In Modal - for transitioning from waitlist to checked in */}
      {showWaitlistCheckInModal && waitlistCheckInAppointment && (
        <WaitlistCheckInModal
          appointment={waitlistCheckInAppointment}
          teamMembers={teamMembers}
          onClose={() => {
            setShowWaitlistCheckInModal(false);
            setWaitlistCheckInAppointment(null);
            setPendingStatusUpdate(null);
          }}
          onSave={async () => {
            // After assigning vet and time, update status to checked in
            if (pendingStatusUpdate && waitlistCheckInAppointment) {
              try {
                await onUpdateStatus(waitlistCheckInAppointment.id, pendingStatusUpdate);
              } catch (error) {
                console.error('Failed to update appointment status:', error);
                alert('Failed to update appointment status. Please try again.');
              }
            }
            setShowWaitlistCheckInModal(false);
            setWaitlistCheckInAppointment(null);
            setPendingStatusUpdate(null);
          }}
        />
      )}
    </div>
  );
};

export default PatientFlowView;

